// loading — the per-vessel loading checklist. Vessels (SAP / model / name / delivery / "left") come from
// the overview; each item's status / comment / date lives in D1. Items that have a matching row in the
// boat's PDI block sync both ways with the PDI sheets: Loaded ↔ PDI Yes, N/A ↔ PDI No, Pending ↔ PDI blank,
// and the latest change wins. A PDI Hold keeps the item pending here. Each boat shows its PDI line and each
// item a small sync marker; the page refreshes itself, so a PDI change shows up without pressing Sync.
// filters: delivery status + model + search; ?sap= opens one boat (the PDI page links here).

import { useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  LOADING_ITEMS,
  loadingProgress,
  normalizePdiStatus,
  parseDeliveryDate,
  timeMs,
  type ItemSyncResult,
  type LoadingPdiBoat,
  type LoadingStatus,
} from '@technohull/shared';
import { useLoading, useOverview, usePushLoadingToPdi, useSetLoadingBulk, useSetLoadingItem, useDeletePhoto, usePhotos, useUploadPhoto, type LoadingResponse, type PhotoMeta } from '../features/hooks.js';
import { markFor, markView, noticeFor, type Mark, type Notice } from '../features/loadingSync.js';
import { useAuth } from '../auth/AuthContext.js';
import { PhotoStrip, EMPTY_PHOTOS } from '../components/PhotoStrip.js';
import { ErrorState, FilterBar, FilterSelect, InlineError, LoadingState, Modal, PageHeader, SearchInput, Segmented, SyncLabel } from '../components/ui.js';
import { IconAlertTriangle, IconCheck, IconChevronRight, IconClose, IconRefresh } from '../components/icons.js';
import { useDateFormatter, useFormatDate } from '../lib/format.js';
import { useIsPhone } from '../lib/media.js';
import { fold, hasText } from '../lib/search.js';

const STATUS_LABEL: Record<LoadingStatus, string> = { pending: 'Pending', loaded: 'Loaded', na: 'N/A' };
// the phone segmented controls (per item and "set all"), in status order
const STATUS_OPTIONS: Array<{ value: LoadingStatus; label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'loaded', label: 'Loaded' },
  { value: 'na', label: 'N/A' },
];

// ── PDI sync markers ──────────────────────────────────────────────────────────
// the rules (which marker, what it says) live in features/loadingSync.ts

function SyncMark({ mark, pdiValue, ambiguous, by, at, canEditPdi, paused }: {
  mark: Mark;
  pdiValue: string | null | undefined;
  ambiguous: boolean;
  by?: string;
  at?: string;
  canEditPdi: boolean;
  paused?: boolean;
}) {
  const df = useDateFormatter();
  const view = markView(mark, pdiValue, { canEditPdi, paused });
  let title = view.note;
  if (mark === 'in-sync' && by?.startsWith('pdi')) {
    const ms = timeMs(at);
    const when = ms === null ? '' : ` on ${df.dateTime(ms)}`;
    title += `. Set from PDI${by.startsWith('pdi:') ? ` by ${by.slice(4)}` : ''}${when}`;
  }
  if (ambiguous) title += '. Two PDI rows have this name; the first is used';
  const icon =
    view.icon === 'check' ? <IconCheck size={12} /> :
    view.icon === 'hatch' ? <i className="pdi-hatch" aria-hidden="true" /> :
    view.icon === 'refresh' ? <IconRefresh size={12} /> : null;
  return (
    <span className={`load-sync ${view.tone}`} title={title}>
      {icon}
      <span className="load-sync-txt">{view.label}</span>
      {ambiguous && <IconAlertTriangle size={12} className="load-sync-amb" aria-label="Two PDI rows have this name" />}
    </span>
  );
}

function LoadNotice({ notice, onDismiss }: { notice: Notice; onDismiss: () => void }) {
  return (
    <div className={`load-notice is-${notice.tone}`} role="status">
      {notice.tone === 'ok' ? <IconCheck size={14} /> : notice.tone === 'warn' ? <i className="pdi-hatch" aria-hidden="true" /> : <IconAlertTriangle size={14} />}
      <span className="load-notice-txt">{notice.text}</span>
      <button type="button" className="load-notice-x" aria-label="Dismiss" onClick={onDismiss}><IconClose size={14} /></button>
    </div>
  );
}

/** "Send to PDI" when some of the waiting items are on hold in PDI: send them all (clearing those holds),
 *  send only the rest, or stop. */
function PushDialog({ total, holds, busy, onClose, onSend }: {
  total: number;
  holds: number;
  busy: boolean;
  onClose: () => void;
  onSend: (includeHolds: boolean) => void;
}) {
  const rest = total - holds;
  return (
    <Modal
      title="Send to PDI"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-danger" disabled={busy} onClick={() => onSend(true)}>Clear hold{holds === 1 ? '' : 's'} too</button>
          {rest > 0 && <button className="btn btn-primary" disabled={busy} onClick={() => onSend(false)}>Send the other {rest}</button>}
        </>
      }
    >
      <div className="confirm-msg">
        {holds === total
          ? `${holds === 1 ? 'This item is' : `All ${holds} items are`} on hold in PDI. Sending ${holds === 1 ? 'it' : 'them'} replaces the hold with the Loading status.`
          : `${holds} of these ${total} items ${holds === 1 ? 'is' : 'are'} on hold in PDI. Sending ${holds === 1 ? 'it' : 'them'} replaces the hold with the Loading status; the other ${rest} only fill in PDI.`}
      </div>
    </Modal>
  );
}

/** the boat's PDI line in its band: the boat's PDI status, how many items matched and are waiting, the
 *  "Send to PDI" button and the way to the boat in the full PDI list. `lead` puts the loaded count first
 *  (phones, so the two chips share a row). */
function PdiLine({ sap, boat, waiting, waitingHolds, canPush, pushing, onPush, lead }: {
  sap: string;
  boat: LoadingPdiBoat;
  lead?: ReactNode;
  waiting: number;
  waitingHolds: number;
  canPush: boolean;
  pushing: boolean;
  onPush: () => void;
}) {
  const stop = (e: MouseEvent) => e.stopPropagation();
  const label = `${boat.sheet}${boat.season ? ` · ${boat.season}` : ''}`;
  const parts = [`${boat.matched} of ${LOADING_ITEMS.length} items matched`];
  if (waiting) parts.push(`${waiting} not in PDI yet`);
  if (boat.holds) parts.push(`${boat.holds} ${boat.holds === 1 ? 'hold' : 'holds'} in PDI`);
  return (
    <div className="load-pdi">
      {lead}
      {boat.status === 'Hold' ? (
        <span className="pill hold load-pdi-st"><i className="pdi-hatch" aria-hidden="true" />PDI on hold</span>
      ) : boat.status === 'Yes' ? (
        <span className="pill ok load-pdi-st"><IconCheck size={12} />PDI signed off</span>
      ) : (
        <span className="pill load-pdi-st">PDI open</span>
      )}
      <span className="load-pdi-meta" title={`PDI sheet ${label}`}>{parts.join(' · ')}</span>
      {canPush && waiting > 0 && (
        <button
          type="button"
          className="btn btn-sm load-pdi-push"
          disabled={pushing}
          title={waitingHolds ? `${waitingHolds} of them ${waitingHolds === 1 ? 'is' : 'are'} on hold in PDI; you'll be asked about ${waitingHolds === 1 ? 'it' : 'those'}` : 'Write these Loading statuses to the PDI sheet'}
          onClick={(e) => { stop(e); onPush(); }}
        >
          {pushing ? 'Sending…' : `Send ${waiting} to PDI`}
        </button>
      )}
      <Link
        className="load-pdi-link"
        to={`/pdi?view=full&sheet=${encodeURIComponent(boat.sheetId)}&sap=${encodeURIComponent(sap)}&label=${encodeURIComponent(label)}`}
        onClick={stop}
      >
        Open PDI<IconChevronRight size={12} />
      </Link>
    </div>
  );
}

export function Loading() {
  const overview = useOverview();
  const loading = useLoading();
  const setItem = useSetLoadingItem();
  const setBulk = useSetLoadingBulk();
  const push = usePushLoadingToPdi();
  const photos = usePhotos('loading');
  const uploadPhoto = useUploadPhoto();
  const deletePhoto = useDeletePhoto();
  const { can } = useAuth();
  const canWrite = can('overview:write');
  const canEditPdi = can('pdi:write');
  // the PDI block only comes to people who may read PDI; the server leaves it out for the rest
  const pdi = can('pdi:read') ? loading.data?.pdi ?? null : null;
  const fmtDate = useFormatDate();
  const isPhone = useIsPhone();

  const [q, setQ] = useState('');
  const [delivery, setDelivery] = useState<'current' | 'all' | 'delivered'>('current');
  const [model, setModel] = useState('all');
  const [openSap, setOpenSap] = useState<Set<string>>(new Set());
  // per boat: what became of PDI after the last save there (failures and kept holds only)
  const [notices, setNotices] = useState<Record<string, Notice>>({});
  const [pushAsk, setPushAsk] = useState<{ sap: string; total: number; holds: number } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const note = (sap: string, n: Notice | null) =>
    setNotices((p) => {
      const next = { ...p };
      if (n) next[sap] = n;
      else delete next[sap];
      return next;
    });

  // ?sap= (from the PDI page): show that boat whatever the filters, open it and scroll to it, once
  const [params] = useSearchParams();
  const focusSap = params.get('sap')?.trim() || '';
  const focused = useRef('');
  const [scrollTo, setScrollTo] = useState('');
  useEffect(() => {
    if (!focusSap || focused.current === focusSap || !overview.data || !loading.data) return;
    focused.current = focusSap;
    setQ(focusSap);
    setDelivery('all');
    setModel('all');
    setOpenSap(new Set([focusSap]));
    setScrollTo(focusSap);
  }, [focusSap, overview.data, loading.data]);
  useEffect(() => {
    if (!scrollTo) return;
    const id = requestAnimationFrame(() => {
      document.getElementById(`load-${scrollTo}`)?.scrollIntoView({ block: 'start' });
      setScrollTo('');
    });
    return () => cancelAnimationFrame(id);
  }, [scrollTo, q]);

  // `${sap}:${itemNo}` → stored state (a missing entry = default 'pending').
  const stateBy = useMemo(() => {
    const m = new Map<string, LoadingResponse['items'][number]>();
    for (const it of loading.data?.items ?? []) m.set(`${it.sap}:${it.itemNo}`, it);
    return m;
  }, [loading.data]);

  // `${sap}:${itemNo}` → its photos.
  const photosByRef = useMemo(() => {
    const m = new Map<string, PhotoMeta[]>();
    for (const p of photos.data?.items ?? []) { const a = m.get(p.refKey) ?? []; a.push(p); m.set(p.refKey, a); }
    return m;
  }, [photos.data]);

  const models = useMemo(() => {
    const set = new Set<string>();
    for (const v of overview.data?.vessels ?? []) if (v.name && v.model) set.add(v.model);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [overview.data]);

  const vessels = useMemo(() => {
    let vs = (overview.data?.vessels ?? []).filter((v) => v.name && v.sap);
    if (delivery === 'current') vs = vs.filter((v) => !v.left);
    else if (delivery === 'delivered') vs = vs.filter((v) => v.left);
    if (model !== 'all') vs = vs.filter((v) => v.model === model);
    const query = fold(q.trim());
    if (query) vs = vs.filter((v) => hasText(query, v.name, v.sap, v.model));
    return vs;
  }, [overview.data, delivery, model, q]);

  const activeFilters = (q.trim() ? 1 : 0) + (delivery !== 'current' ? 1 : 0) + (model !== 'all' ? 1 : 0);
  const clearFilters = () => { setQ(''); setDelivery('current'); setModel('all'); };

  const toggleOpen = (sap: string) =>
    setOpenSap((p) => { const n = new Set(p); n.has(sap) ? n.delete(sap) : n.add(sap); return n; });

  const statusOf = (sap: string, i: number): LoadingStatus => stateBy.get(`${sap}:${i}`)?.status ?? 'pending';
  const progress = (sap: string) => loadingProgress((i) => stateBy.get(`${sap}:${i}`)?.status);
  const markOf = (sap: string, i: number): Mark => {
    const b = pdi?.boats[sap];
    return b ? markFor(b.sync[i], b.pdi[i], statusOf(sap, i)) : 'none';
  };

  // one item: only the field that changed is sent. A status change reports what became of PDI.
  // (mutateAsync, not mutate's callbacks: those only fire for the last of several quick edits, and every
  // edit's PDI outcome counts. A failed save shows through the mutation's own error.)
  // A save that synced cleanly leaves an earlier notice up; only its own close button takes it away.
  const quiet = () => undefined;
  const report = (sap: string, n: Notice | null) => { if (n) note(sap, n); };
  const set = (sap: string, itemNo: number, patch: { status?: LoadingStatus; comment?: string; date?: string }) => {
    setItem.mutateAsync({ sap, itemNo, ...patch }).then((res) => { if (res.pdi) report(sap, noticeFor([[itemNo, res.pdi]])); }, quiet);
  };
  const setAll = (sap: string, status: LoadingStatus) => {
    if (setBulk.isPending) return;
    setBulk.mutateAsync({ sap, status }).then((res) => report(sap, noticeFor(res.pdi.map((r, i) => [i, r] as const))), quiet);
  };
  const sendToPdi = (sap: string, includeHolds: boolean) => {
    push
      .mutateAsync({ sap, includeHolds })
      .then((res) => note(sap, noticeFor(Object.entries(res.pdi).map(([i, r]) => [Number(i), r] as [number, ItemSyncResult]), { push: true })), quiet)
      .finally(() => setPushAsk(null));
  };
  const askPush = (sap: string) => {
    const b = pdi?.boats[sap];
    if (!b) return;
    let total = 0;
    let holds = 0;
    for (let i = 0; i < LOADING_ITEMS.length; i++) {
      if (markOf(sap, i) !== 'not-in-pdi') continue;
      total++;
      if (normalizePdiStatus(b.pdi[i]) === 'Hold') holds++;
    }
    if (!total) return;
    if (holds) setPushAsk({ sap, total, holds });
    else sendToPdi(sap, false);
  };

  const busy = overview.isLoading || loading.isLoading;
  // boats PDI changes can't reach just now (a PDI sheet before theirs couldn't be read fresh)
  const pdiPaused = useMemo(() => (pdi ? Object.values(pdi.boats).filter((b) => b.paused).length : 0), [pdi]);

  return (
    <>
      <PageHeader title="Loading">
        <span className="muted page-count">{vessels.length} vessel{vessels.length === 1 ? '' : 's'}</span>
        {/* the page refreshes itself every 30s; the button only says "Syncing…" when pressed, so the
            background refresh doesn't make it flicker */}
        <button
          className="btn btn-sm"
          disabled={syncing}
          onClick={() => { setSyncing(true); void Promise.allSettled([overview.refetch(), loading.refetch()]).finally(() => setSyncing(false)); }}
        >
          <SyncLabel busy={syncing} />
        </button>
      </PageHeader>
      <FilterBar search={<SearchInput value={q} onChange={setQ} placeholder={isPhone ? 'Search vessels' : 'Search vessel / SAP / model'} width={220} />} activeCount={activeFilters} onClear={clearFilters}>
        <FilterSelect
          label="Delivery"
          ariaLabel="Delivery status"
          value={delivery}
          onChange={(d) => setDelivery(d as 'current' | 'all' | 'delivered')}
          options={[{ value: 'current', label: 'Current production' }, { value: 'all', label: 'All' }, { value: 'delivered', label: 'Delivered' }]}
        />
        <FilterSelect
          label="Model"
          value={model}
          onChange={setModel}
          options={[{ value: 'all', label: 'All models' }, ...models.map((m) => ({ value: m, label: m }))]}
        />
      </FilterBar>

      {busy && <LoadingState label="Loading…" />}
      {overview.error && <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />}
      {loading.error && <ErrorState error={loading.error} onRetry={() => void loading.refetch()} />}
      {pdi && !pdi.ok && (
        <div className="load-notice is-warn load-notice-page" role="status">
          <IconAlertTriangle size={14} />
          <span className="load-notice-txt">PDI couldn't be read just now, so the PDI markers are missing. Loading still saves, and the markers come back on the next refresh.</span>
        </div>
      )}
      {pdi && pdi.ok && (pdi.partial || pdiPaused > 0) && (
        <div className="load-notice is-warn load-notice-page" role="status">
          <IconAlertTriangle size={14} />
          <span className="load-notice-txt">
            {pdi.partial ? `Some PDI sheets couldn't be read just now${pdi.error ? ` (${pdi.error})` : ''}, so boats on them may show no PDI block.` : "Some PDI sheets couldn't be read fresh just now."}
            {pdiPaused > 0 ? ` PDI changes for ${pdiPaused === 1 ? 'one boat' : `${pdiPaused} boats`} reach Loading once they can be read.` : ''} Loading still saves.
          </span>
        </div>
      )}
      {setItem.isError && <InlineError error={setItem.error} onDismiss={() => setItem.reset()} />}
      {setBulk.isError && <InlineError error={setBulk.error} onDismiss={() => setBulk.reset()} />}
      {push.isError && <InlineError error={push.error} onDismiss={() => push.reset()} />}
      {uploadPhoto.isError && <InlineError error={uploadPhoto.error} onDismiss={() => uploadPhoto.reset()} />}
      {deletePhoto.isError && <InlineError error={deletePhoto.error} onDismiss={() => deletePhoto.reset()} />}

      {overview.data && loading.data && vessels.map((v) => {
        const p = progress(v.sap);
        const open = openSap.has(v.sap);
        const boat = pdi?.boats[v.sap];
        const marks = LOADING_ITEMS.map((_, i) => markOf(v.sap, i));
        const ambiguous = new Set(boat?.ambiguous ?? []);
        const waiting = marks.filter((m) => m === 'not-in-pdi').length;
        const waitingHolds = boat ? marks.filter((m, i) => m === 'not-in-pdi' && normalizePdiStatus(boat.pdi[i]) === 'Hold').length : 0;
        const mark = (i: number, extra?: { by?: string; at?: string }): ReactNode =>
          pdi && <SyncMark mark={marks[i]!} pdiValue={boat?.pdi[i]} ambiguous={ambiguous.has(i)} canEditPdi={canEditPdi} paused={boat?.paused} {...extra} />;
        // the "set all" segmented shows a status only while every item shares it
        const allStatus: LoadingStatus | '' =
          p.loadedN === LOADING_ITEMS.length ? 'loaded' : p.na === LOADING_ITEMS.length ? 'na' : p.loadedN === 0 && p.na === 0 ? 'pending' : '';
        const notice = notices[v.sap];
        const loadedPill = (
          <span className={`pill load-prog ${p.done ? 'ok' : p.loadedN ? 'warn' : ''}`}>
            {p.applicable === 0 ? 'All N/A' : `${p.loadedN}/${p.applicable} loaded`}{p.na && p.applicable > 0 ? ` · ${p.na} N/A` : ''}
          </span>
        );
        // phones: the loaded count rides at the head of the PDI line, so it shares a row with the PDI chip
        const lead = isPhone && pdi?.ok ? loadedPill : null;
        return (
          <div key={v.sap} id={`load-${v.sap}`} className="load-group">
            <div className="load-ghd" onClick={() => toggleOpen(v.sap)}>
              <button
                type="button"
                className={`load-chev${open ? ' is-open' : ''}`}
                aria-expanded={open}
                aria-label={`${open ? 'Collapse' : 'Expand'} ${v.name}`}
                onClick={(e) => { e.stopPropagation(); toggleOpen(v.sap); }}
              ><IconChevronRight size={14} /></button>
              <div className="load-who">
                <div className="load-name">{v.name}</div>
                <div className="load-meta">
                  {v.sap} · {v.model}{v.deliveryDate ? ` · DEL ${parseDeliveryDate(v) ? fmtDate(v.deliveryDate) : v.deliveryDate}` : ''}{v.left ? ' · delivered' : ''}
                </div>
                {pdi && boat && (
                  <PdiLine
                    lead={lead}
                    sap={v.sap}
                    boat={boat}
                    waiting={waiting}
                    waitingHolds={waitingHolds}
                    // a paused boat's block might be on a sheet that couldn't be read, so nothing is sent to it
                    canPush={canWrite && canEditPdi && !boat.paused}
                    pushing={push.isPending && push.variables?.sap === v.sap}
                    onPush={() => askPush(v.sap)}
                  />
                )}
                {pdi && pdi.ok && !boat && (
                  <div className="load-pdi">
                    {lead}
                    <span
                      className="load-pdi-none"
                      title={pdi.partial
                        ? `Some PDI sheets couldn't be read, so SAP ${v.sap} may be on one of those.`
                        : `None of the PDI sheets (this season or earlier) has a block with SAP ${v.sap}. Once PDI starts, check the SAP there if this stays.`}
                    >
                      {pdi.partial ? 'PDI block not found (some PDI sheets couldn\'t be read)' : 'No PDI block for this SAP'}
                    </span>
                  </div>
                )}
              </div>
              {!lead && loadedPill}
            </div>
            {notice && <LoadNotice notice={notice} onDismiss={() => note(v.sap, null)} />}
            {open && isPhone && (
              // phones: a card per item (status, photos, then comment and date behind Details)
              <div className="load-body">
                {canWrite && (
                  <div className="load-bulk">
                    <span className="load-bulk-lbl">Set all items to</span>
                    <div className={`load-seg is-${allStatus || 'mixed'}`}>
                      <Segmented
                        value={allStatus}
                        // tapping the option already shown changes nothing, so it sends nothing
                        onChange={(s) => { if (s && s !== allStatus) setAll(v.sap, s); }}
                        ariaLabel={`Set all items of ${v.name} to`}
                        options={STATUS_OPTIONS}
                      />
                    </div>
                  </div>
                )}
                <ol className="load-cards">
                  {LOADING_ITEMS.map((item, i) => {
                    const st = stateBy.get(`${v.sap}:${i}`);
                    const status = st?.status ?? 'pending';
                    const peek = [st?.comment, st?.date ? fmtDate(st.date) || st.date : ''].filter(Boolean).join(' · ');
                    const id = `load-${v.sap}-${i}`;
                    const m = marks[i]!;
                    // touch has no tooltips, so a state that needs explaining says so under the status
                    const explain = m === 'pdi-hold' || m === 'not-in-pdi' || m === 'unknown' || (m === 'pdi-newer' && !!boat?.paused);
                    const why = pdi && (explain || ambiguous.has(i))
                      ? [explain ? markView(m, boat?.pdi[i], { canEditPdi, paused: boat?.paused }).note : '', ambiguous.has(i) ? 'Two PDI rows have this name; the first is used' : ''].filter(Boolean).join('. ')
                      : '';
                    return (
                      <li key={i} className={`load-card is-${status}`}>
                        <div className="load-card-hd">
                          <span className="load-no">{i + 1}</span>
                          <span className="load-item">{item}</span>
                          {mark(i, { by: st?.by, at: st?.at })}
                        </div>
                        {canWrite ? (
                          <div className={`load-seg is-${status}`}>
                            <Segmented value={status} onChange={(s) => { if (s !== status) set(v.sap, i, { status: s }); }} ariaLabel={`Status - ${item}`} options={STATUS_OPTIONS} />
                          </div>
                        ) : (
                          <span className={`pill load-stat ${status === 'loaded' ? 'ok' : status === 'pending' ? 'warn' : ''}`}>{STATUS_LABEL[status]}</span>
                        )}
                        {why && <div className="load-sync-note">{why}.</div>}
                        <PhotoStrip
                          photos={photosByRef.get(`${v.sap}:${i}`) ?? EMPTY_PHOTOS}
                          canWrite={canWrite}
                          busy={uploadPhoto.isPending}
                          onUpload={(f) => uploadPhoto.mutate({ entity: 'loading', refKey: `${v.sap}:${i}`, file: f })}
                          onDelete={(pid) => deletePhoto.mutate({ id: pid, entity: 'loading' })}
                          deleteMessage={`Delete this photo from “${item}”?`}
                        />
                        {canWrite ? (
                          <details className="load-details">
                            <summary>
                              <IconChevronRight size={14} className="load-details-chev" />
                              Details
                              {peek && <span className="load-details-peek">{peek}</span>}
                            </summary>
                            <div className="load-details-bd">
                              <label className="field-label" htmlFor={`${id}-c`}>Comment</label>
                              <input
                                id={`${id}-c`}
                                key={st?.comment ?? ''}
                                className="field"
                                defaultValue={st?.comment ?? ''}
                                placeholder="Add a note…"
                                onBlur={(e) => { if (e.target.value !== (st?.comment ?? '')) set(v.sap, i, { comment: e.target.value }); }}
                              />
                              <label className="field-label" htmlFor={`${id}-d`}>Date</label>
                              <input id={`${id}-d`} className="field" type="date" value={st?.date ?? ''} onChange={(e) => set(v.sap, i, { date: e.target.value })} />
                            </div>
                          </details>
                        ) : (
                          peek && <div className="load-ro">{peek}</div>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}
            {open && !isPhone && (
              <div className="tbl-wrap">
                {canWrite && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 2px' }}>
                    <span className="muted" style={{ fontSize: 11 }}>Set all items to:</span>
                    <button className="btn btn-sm" disabled={setBulk.isPending} onClick={() => setAll(v.sap, 'loaded')}>Loaded</button>
                    <button className="btn btn-sm" disabled={setBulk.isPending} onClick={() => setAll(v.sap, 'pending')}>Pending</button>
                    <button className="btn btn-sm" disabled={setBulk.isPending} onClick={() => setAll(v.sap, 'na')}>N/A</button>
                  </div>
                )}
                <table className="tbl">
                  <thead>
                    <tr>
                      <th style={{ width: 30 }}>#</th>
                      <th>Item</th>
                      <th style={{ width: 120 }}>Status</th>
                      {pdi && <th style={{ width: 150 }}>PDI</th>}
                      <th>Comment</th>
                      <th style={{ width: 140 }}>Date</th>
                      <th style={{ width: 150 }}>Photos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {LOADING_ITEMS.map((item, i) => {
                      const st = stateBy.get(`${v.sap}:${i}`);
                      const status = st?.status ?? 'pending';
                      return (
                        <tr key={i} className={status === 'na' ? 'load-row-na' : undefined}>
                          <td className="muted">{i + 1}</td>
                          <td style={{ fontWeight: status === 'loaded' ? 400 : 500 }}>{item}</td>
                          <td>
                            {canWrite ? (
                              <select
                                className={`field load-in load-sel is-${status}`}
                                value={status}
                                onChange={(e) => set(v.sap, i, { status: e.target.value as LoadingStatus })}
                                aria-label={`Status - ${item}`}
                              >
                                <option value="pending">Pending</option>
                                <option value="loaded">Loaded</option>
                                <option value="na">N/A</option>
                              </select>
                            ) : (
                              STATUS_LABEL[status]
                            )}
                          </td>
                          {pdi && <td className="load-pdi-cell">{mark(i, { by: st?.by, at: st?.at })}</td>}
                          <td>
                            {canWrite ? (
                              <input
                                key={st?.comment ?? ''}
                                className="field load-in load-comment"
                                defaultValue={st?.comment ?? ''}
                                placeholder="-"
                                aria-label={`Comment - ${item}`}
                                onBlur={(e) => { if (e.target.value !== (st?.comment ?? '')) set(v.sap, i, { comment: e.target.value }); }}
                              />
                            ) : (
                              st?.comment || '-'
                            )}
                          </td>
                          <td>
                            {canWrite ? (
                              <input
                                className="field load-in"
                                type="date"
                                value={st?.date ?? ''}
                                onChange={(e) => set(v.sap, i, { date: e.target.value })}
                                aria-label={`Date - ${item}`}
                              />
                            ) : (
                              fmtDate(st?.date) || st?.date || '-'
                            )}
                          </td>
                          <td>
                            <PhotoStrip
                              photos={photosByRef.get(`${v.sap}:${i}`) ?? EMPTY_PHOTOS}
                              canWrite={canWrite}
                              busy={uploadPhoto.isPending}
                              onUpload={(f) => uploadPhoto.mutate({ entity: 'loading', refKey: `${v.sap}:${i}`, file: f })}
                              // PhotoStrip asks before deleting; this only words its question
                              onDelete={(id) => deletePhoto.mutate({ id, entity: 'loading' })}
                              deleteMessage={`Delete this photo from “${item}”?`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {overview.data && loading.data && vessels.length === 0 && (
        <div className="muted" style={{ padding: 20, textAlign: 'center' }}>No vessels match the current filters.</div>
      )}
      {pushAsk && (
        <PushDialog
          total={pushAsk.total}
          holds={pushAsk.holds}
          busy={push.isPending}
          onClose={() => setPushAsk(null)}
          onSend={(includeHolds) => sendToPdi(pushAsk.sap, includeHolds)}
        />
      )}
    </>
  );
}
